import tw, { styled } from 'twin.macro';

export const ContentContainer = styled.div`
  ${tw`p-4 sm:p-5 flex flex-col gap-4 w-full`}
`;

export const Card = styled.div`
  ${tw`bg-white rounded-xl border border-neutral-40 overflow-hidden min-w-0`}
`;

// --- Card 1: Tabs + Timeline + Period cost ---

export const TabContainer = styled.div`
  ${tw`flex border-b border-neutral-40 py-2`}
`;

export const TabButton = styled.button<{ isActive: boolean }>`
  ${tw`px-4 py-2.5 border-none bg-transparent text-sm font-semibold cursor-pointer`}
  ${({ isActive }) =>
    isActive
      ? tw`text-neutral-900 border-b-2 border-neutral-900 -mb-px`
      : tw`text-neutral-200 border-b-2 border-transparent -mb-px`}
  font-family: inherit;
`;

export const TimelineScrollContainer = styled.div`
  ${tw`overflow-x-auto max-w-full`}
`;

export const TimelineInner = styled.div`
  ${tw`px-4 pt-4 pb-6`}
`;

export const MonthHeaderCell = styled.div`
  ${tw`text-xs font-semibold text-neutral-700 pb-1 text-left px-1`}
`;

export const DayHeaderCell = styled.div<{ isToday: boolean; isFuture: boolean }>`
  ${tw`text-center text-xs pb-2 font-sans`}
  ${({ isToday }) => (isToday ? tw`font-bold text-neutral-900` : tw`font-medium text-neutral-200`)}
  ${({ isFuture }) => isFuture && tw`text-neutral-40`}
  ${({ isToday }) => (isToday ? `border-bottom: 2px solid currentColor;` : `border-bottom: 1px solid #DEDEE1;`)}
`;

export const JobRow = styled.div<{ $isLast?: boolean; $isFirst?: boolean }>`
  ${tw`flex items-center`}
  ${({ $isFirst }) => $isFirst && tw`pt-6`}
  ${({ $isLast }) => !$isLast && tw`border-b border-neutral-40`}
`;

export const JobLabel = styled.div`
  ${tw`py-3 flex items-center gap-2 flex-shrink-0 bg-white z-10`}
  position: sticky;
  left: 0;
  width: 156px;
  padding-left: 16px;
  margin-left: -16px;
`;

export const JobDot = styled.div<{ color: string }>`
  ${tw`w-2 h-2 rounded-full flex-shrink-0`}
  background: ${({ color }) => color};
`;

export const JobName = styled.span`
  ${tw`text-sm font-medium text-neutral-700 overflow-hidden whitespace-nowrap`}
  text-overflow: ellipsis;
  max-width: 120px;
`;

export const BarCell = styled.div`
  ${tw`h-9 flex items-center justify-center relative`}
`;

export const Bar = styled.div<{
  color: string;
  isHovered: boolean;
  roundLeft: boolean;
  roundRight: boolean;
}>`
  ${tw`w-full transition-opacity duration-100`}
  height: 22px;
  background: ${({ color }) => color};
  opacity: ${({ isHovered }) => (isHovered ? 1 : 0.8)};
  border-radius: ${({ roundLeft, roundRight }) =>
    `${roundLeft ? 5 : 0}px ${roundRight ? 5 : 0}px ${roundRight ? 5 : 0}px ${roundLeft ? 5 : 0}px`};
  margin-left: ${({ roundLeft }) => (roundLeft ? '2px' : '0')};
  margin-right: ${({ roundRight }) => (roundRight ? '2px' : '0')};
`;

export const TooltipContent = styled.div`
  ${tw`flex flex-col gap-1`}
`;

export const TooltipDate = styled.div`
  ${tw`font-semibold mb-1 text-xs`}
`;

export const TooltipJobItem = styled.div`
  ${tw`flex items-center gap-1.5 mb-0.5 text-xs`}

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
  }
`;

export const TooltipCost = styled.div`
  ${tw`mt-1 pt-1 font-semibold text-xs`}
  border-top: 1px solid rgba(255, 255, 255, 0.15);
`;

export const PeriodCostRow = styled.div`
  ${tw`px-4 py-3.5 border-t border-neutral-40 flex items-center justify-between`}
`;

export const PeriodCostLabel = styled.span`
  ${tw`text-sm text-neutral-900 font-medium`}
`;

export const PeriodCostValue = styled.span`
  ${tw`text-lg font-medium text-neutral-900`}
`;

export const EmptyState = styled.div`
  ${tw`py-6 text-center text-neutral-200 text-sm`}
`;

// --- Card 2: All-time summary ---

export const SectionTitle = styled.div`
  ${tw`px-4 py-3.5 border-b border-neutral-40`}
`;

export const SectionTitleText = styled.span`
  ${tw`text-sm font-bold text-neutral-900`}
`;

export const SectionSubtitle = styled.span`
  ${tw`text-sm text-neutral-200`}
`;

export const SummaryItemsContainer = styled.div`
  ${tw`py-2`}
  min-height: 420px;
`;

export const SummaryItem = styled.div<{ hasBorder: boolean }>`
  ${tw`px-4 flex items-center gap-3 py-3`}
  ${({ hasBorder }) => hasBorder && tw`border-b border-neutral-40`}
`;

export const SummaryItemInfo = styled.div`
  ${tw`flex-1 min-w-0`}
`;

export const SummaryItemDate = styled.div`
  ${tw`text-xs text-neutral-200 mt-0.5`}
`;

export const SummaryItemStats = styled.div`
  ${tw`text-right flex-shrink-0`}
`;

export const SummaryItemDays = styled.div`
  ${tw`text-lg font-bold text-neutral-900 leading-none`}
`;

export const SummaryItemDaysLabel = styled.span`
  ${tw`text-xs font-medium text-neutral-900`}
`;

export const SummaryItemCost = styled.div`
  ${tw`text-sm text-neutral-200 mt-1`}
`;

export const PaginationContainer = styled.div`
  ${tw`px-4 py-2.5 border-t border-neutral-40 flex items-center justify-between`}
`;

export const PaginationInfo = styled.span`
  ${tw`text-xs text-neutral-200`}
`;

export const PaginationButtons = styled.div`
  ${tw`flex gap-1`}
`;

export const PaginationButton = styled.button<{ isDisabled: boolean }>`
  ${tw`w-8 h-8 rounded-lg border border-neutral-40 bg-white flex items-center justify-center`}
  ${({ isDisabled }) => (isDisabled ? tw`cursor-not-allowed opacity-30` : tw`cursor-pointer opacity-100`)}
`;
